import { memo, useMemo } from 'react';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { SidebarItem } from '@reflex/ui/components/sidebar/SidebarItem';
import type { MenuActionItem } from '@reflex/ui/components/sidebar/SidebarItem';
import { Button } from '@reflex/ui/components/ui/button';
import { ArchiveRestore, Trash2 } from 'lucide-react';
import { AgentActivityIcon } from '@/components/status/agent/AgentActivityIcon';
import { AgentOwnerAvatar } from '@/components/agent/AgentOwnerAvatar';
import { AgentMetaLine } from '@/components/agent/AgentMetaLine';
import { agentToMetaLineProps } from '@/components/agent/agent-meta-line-props';
import { useAuth } from '@/hooks/auth/useAuth';
import { activityFromDurableAgentState } from '@/hooks/agents/agent-activity-utils';
import { timeAgo } from '@reflex/ui/lib/format';
import type { Agent } from '@/lib/types';

export interface ArchivedAgentRowProps {
  agent: Agent;
  selected: boolean;
  onSelect: (agent: Agent) => void;
  onUnarchive: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
  /** Multi-select mode is active: show a checkbox and toggle selection on click. */
  selectionMode?: boolean;
  /** Whether this row is checked in the current multi-selection. */
  checked?: boolean;
  /** Toggle this row's selection; `shiftKey` supports range selection. */
  onToggleSelect?: (agent: Agent, shiftKey: boolean) => void;
}

export const ArchivedAgentRow = memo(function ArchivedAgentRow({
  agent,
  selected,
  onSelect,
  onUnarchive,
  onDelete,
  selectionMode = false,
  checked = false,
  onToggleSelect,
}: ArchivedAgentRowProps) {
  const menuActions: MenuActionItem[] = useMemo(
    () => [
      { label: 'Unarchive', icon: ArchiveRestore, onClick: () => onUnarchive(agent) },
      { separator: true as const },
      { label: 'Delete', icon: Trash2, onClick: () => onDelete(agent), destructive: true },
    ],
    [agent, onUnarchive, onDelete],
  );

  const activity = activityFromDurableAgentState(agent);
  const { user: currentUser } = useAuth();
  const showOwnerAvatar = !!agent.userId && agent.userId !== currentUser?.id;

  return (
    <SidebarItem
      testId={`agent-sidebar-row-${agent.id}`}
      selected={selected}
      onClick={() => onSelect(agent)}
      selectable
      selectionMode={selectionMode}
      checked={checked}
      onToggleSelect={(shiftKey) => onToggleSelect?.(agent, shiftKey)}
      icon={<AgentActivityIcon activity={activity} agentId={agent.id} />}
      dimmed
      actions={
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Unarchive"
              onClick={() => onUnarchive(agent)}
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Unarchive</TooltipContent>
        </Tooltip>
      }
      menuActions={menuActions}
    >
      <>
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {showOwnerAvatar && agent.userId && <AgentOwnerAvatar userId={agent.userId} />}
            <PanelTitle as="h3" size="sm" className="min-w-0 font-normal">
              {agent.name}
            </PanelTitle>
          </div>
          <span className="shrink-0 text-3xs text-muted-foreground">
            {timeAgo(agent.createdAt, { compact: true })}
          </span>
        </div>
        <AgentMetaLine {...agentToMetaLineProps(agent)} />
      </>
    </SidebarItem>
  );
});
